import { CheckCircle, XClose } from '@untitledui/icons';
import { toast } from 'sonner';

import { cx } from '@/utils/cx';

import type { ProposalDetail } from '../core/types';
import { getCompletedActionItems } from './successHelpers';
import { PROPOSAL_TOAST_POSITION } from './toastPosition';

/**
 * Show a non-blocking success toast after an action is approved.
 * For 1-3 actions, displays each with icon, label, and sublabel.
 * For 4+ actions, shows a summary count (e.g. "7 actions completed").
 * Auto-dismisses after 5 seconds.
 */
export function showSuccessToast(proposal: ProposalDetail): void {
  const items = getCompletedActionItems(proposal.actions);

  if (items.length === 0) {
    // Fallback for edge case where no items exist
    toast.success('Action completed');
    return;
  }

  const isSummary = items.length > 3;

  toast.custom(
    t => (
      <div
        className={cx(
          'flex gap-3 p-4 bg-bg-primary border border-border-secondary rounded-xl shadow-lg min-w-[320px] max-w-[420px]',
          isSummary ? 'items-center' : 'items-start'
        )}
      >
        {/* Success checkmark */}
        <div
          className={cx(
            'flex items-center justify-center size-9 rounded-full bg-success-50 shrink-0',
            !isSummary && 'mt-0.5'
          )}
        >
          <CheckCircle className='size-5 text-success-600' aria-hidden='true' />
        </div>

        {/* Content - show all completed items or summary for 4+ */}
        <div className='flex-1 min-w-0 space-y-2'>
          {isSummary ? (
            <p className='text-sm font-semibold text-fg-primary'>
              {`${items.length} actions completed`}
            </p>
          ) : (
            items.map(item => (
              <div key={item.executionOrder} className='min-w-0'>
                <p className='text-sm font-semibold text-fg-primary truncate'>
                  {item.label}
                </p>
                {item.sublabel && (
                  <p className='text-xs text-secondary mt-0.5 truncate'>
                    {item.sublabel}
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Dismiss button */}
        <button
          type='button'
          onClick={() => toast.dismiss(t)}
          className='text-tertiary hover:text-fg-secondary transition-colors p-1 -m-1 rounded-md hover:bg-tertiary shrink-0'
          aria-label='Dismiss'
        >
          <XClose className='size-4' />
        </button>
      </div>
    ),
    {
      duration: 5000,
      position: PROPOSAL_TOAST_POSITION,
      // Override Sonner's default wrapper styles so only the inner card renders
      // (prevents double-border from the Toaster's default border/padding/shadow).
      style: {
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
        padding: 0,
      },
    }
  );
}
